import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { CurrencyCode } from 'entities/currency-code';
import { Discount, DiscountProgram } from 'entities/discount';
import { PartPriceOverride } from 'entities/parts/part-price-override';
import { VerifiedPrice } from 'entities/parts/verified-price';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AppState } from 'store/app-state';
import * as BranchSelectors from "store/branch/branch.selectors";
import * as VerifiedPricesSelectors from "store/verified-prices/verified-prices.selectors";

type DiscountProgramMap = { [program in DiscountProgram]?: Discount };

@Component({
    selector: 'coupons',
    templateUrl: 'coupons.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class CouponsComponent {
  store = inject(Store<AppState>);

  private partSubject: Subject<PartPriceOverride> = new BehaviorSubject(null);
  @Input('part') set partInput(value: PartPriceOverride) {
    this.partSubject.next(value);
  }
  public part$: Observable<PartPriceOverride> = this.partSubject.asObservable();

  private currencyCodeSubject: Subject<Observable<CurrencyCode>> = new BehaviorSubject(null);
  @Input('currencyCode$') set currencyCodeInput(value: Observable<CurrencyCode>) {
    this.currencyCodeSubject.next(value);
  }
  public currencyCode$: Observable<CurrencyCode> = this.currencyCodeSubject.asObservable().pipe(
    switchMap((currencyCodeObs) => currencyCodeObs ?? this.store.select(BranchSelectors.selectCurrencyCode))
  );

  private programDiscountsSubject: Subject<DiscountProgramMap> = new BehaviorSubject<DiscountProgramMap>(null);
  @Input('programDiscounts') set programDiscountsInput(value: DiscountProgramMap) {
    this.programDiscountsSubject.next(value);
  }
  public programDiscountEntities$: Observable<DiscountProgramMap> = this.programDiscountsSubject
    .pipe(
      map((programDiscounts) => programDiscounts ?? {})
    );
  public allProgramDiscounts$: Observable<{ program: string, discounts: Discount }[]> = this.programDiscountEntities$
    .pipe(
      map((entities: DiscountProgramMap) => Object.entries(entities)),
      map((entries: [string, Discount][]) => entries.map(([program, discounts]) => ({ program, discounts })))
    );
  public discountTotal$ = this.allProgramDiscounts$
    .pipe(
      map((allProgramDiscounts: { program: string, discounts: Discount }[]) => allProgramDiscounts
        .flatMap(({ discounts }: { discounts: Discount }) => discounts)
        .reduce((sum: number, discount: Discount) => sum + discount.amount, 0)
      )
    );

  idSuffix: string;
  @Input('idSuffix') set _idSuffix(value: string) {
    if (value) {
      this.idSuffix = `_${value}`;
    }
  }

  verifiedPrices$ = this.part$.pipe(
    switchMap(part => this.store.select(VerifiedPricesSelectors.selectVerifiedPrice(part?.number)))
  );

  public couponTotal$ = combineLatest([this.part$, this.discountTotal$, this.verifiedPrices$])
    .pipe(
      map(([part, discountTotal, verifiedPrice]: [PartPriceOverride, number, VerifiedPrice]) => {
        if(verifiedPrice?.partNumber === part.number){
          const couponTotal = verifiedPrice.coupons.reduce((sum, coupon) => sum + coupon.amount, 0);
          return couponTotal + discountTotal;
        }
        return part.couponTotal + discountTotal;
      })
    );

  public finalPrice$ = combineLatest([this.part$, this.couponTotal$])
    .pipe(
      map(([part, couponTotal]: [PartPriceOverride, number]) => part.lowestPrice - couponTotal)
    );
}
